import React from 'react';
import { ChevronLeft, ChevronRight, Calendar, Clock, MapPin } from 'lucide-react';
import { useApp } from '../context/AppContext';

const timeSlots = ['8:00 AM', '9:00 AM', '10:00 AM', '11:00 AM', '12:00 PM', '1:00 PM', '2:00 PM', '3:00 PM', '4:00 PM', '5:00 PM', '6:00 PM'];

const BookingView = () => {
  const {
    selectedProvider,
    bookingData,
    setBookingData,
    currentMonth,
    setCurrentMonth,
    setView,
    getDaysInMonth,
    isDateAvailable,
    formatDate,
    calculateTotal
  } = useApp();
  
  const p = selectedProvider;
  if (!p) return null;
  
  const calendarDays = getDaysInMonth(currentMonth);
  const months = ['January','February','March','April','May','June','July','August','September','October','November','December'];
  
  return (
    <div className="min-h-screen bg-slate-50">
      <div className="max-w-5xl mx-auto px-4 py-8">
        <button onClick={() => setView('profile')} className="flex items-center gap-2 text-slate-600 hover:text-teal-600 mb-6">
          <ChevronLeft className="w-5 h-5" />Back to Profile
        </button>
        <div className="bg-white rounded-3xl shadow-xl overflow-hidden">
          <div className="bg-gradient-to-r from-teal-600 to-emerald-500 p-6">
            <h1 className="text-2xl font-bold text-white">Book Your Appointment</h1>
            <p className="text-teal-100">with {p.name || `${p.firstName} ${p.lastName}`}</p>
          </div>
          <div className="p-8 grid grid-cols-1 lg:grid-cols-3 gap-8">
            <div className="lg:col-span-2">
              <h2 className="text-xl font-semibold text-slate-800 mb-4">Select Date</h2>
              <div className="flex justify-between mb-4">
                <button onClick={() => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1))} className="p-2 hover:bg-slate-100 rounded-lg">
                  <ChevronLeft className="w-5 h-5" />
                </button>
                <h3 className="text-lg font-semibold">{months[currentMonth.getMonth()]} {currentMonth.getFullYear()}</h3>
                <button onClick={() => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1))} className="p-2 hover:bg-slate-100 rounded-lg">
                  <ChevronRight className="w-5 h-5" />
                </button>
              </div>
              <div className="grid grid-cols-7 gap-2 mb-6">
                {['Sun','Mon','Tue','Wed','Thu','Fri','Sat'].map(d => <div key={d} className="text-center text-sm font-medium text-slate-400 py-2">{d}</div>)}
                {calendarDays.map((d,i) => (
                  <button 
                    key={i} 
                    disabled={!d || !isDateAvailable(d)} 
                    onClick={() => d && isDateAvailable(d) && setBookingData(prev => ({...prev, date: d}))} 
                    className={`p-3 rounded-xl text-sm font-medium ${!d ? 'invisible' : bookingData.date?.toDateString() === d.toDateString() ? 'bg-teal-600 text-white' : isDateAvailable(d) ? 'bg-slate-50 text-slate-700 hover:bg-teal-50' : 'bg-slate-50 text-slate-300 cursor-not-allowed'}`}
                  >
                    {d?.getDate()}
                  </button>
                ))}
              </div>
              {bookingData.date && (
                <>
                  <h2 className="text-xl font-semibold text-slate-800 mb-4">Select Time</h2>
                  <div className="grid grid-cols-3 sm:grid-cols-4 gap-3 mb-6">
                    {timeSlots.map(t => (
                      <button 
                        key={t} 
                        onClick={() => setBookingData(prev => ({...prev, time: t}))} 
                        className={`p-3 rounded-xl text-sm font-medium ${bookingData.time === t ? 'bg-teal-600 text-white' : 'bg-slate-50 text-slate-700 hover:bg-teal-50'}`}
                      >
                        {t}
                      </button>
                    ))}
                  </div>
                </>
              )}
              {bookingData.time && (
                <>
                  <h2 className="text-xl font-semibold text-slate-800 mb-4">Duration</h2>
                  <div className="flex gap-3 mb-6">
                    {[1,2,3,4].map(h => (
                      <button 
                        key={h} 
                        onClick={() => setBookingData(prev => ({...prev, duration: h}))} 
                        className={`flex-1 p-4 rounded-xl text-sm font-medium ${bookingData.duration === h ? 'bg-teal-600 text-white' : 'bg-slate-50 text-slate-700 hover:bg-teal-50'}`}
                      >
                        {h} {h === 1 ? 'hour' : 'hours'}
                      </button>
                    ))}
                  </div>
                </>
              )}
              <h2 className="text-xl font-semibold text-slate-800 mb-4">Service Address</h2>
              <div className="space-y-4 mb-6">
                <input 
                  type="text" 
                  placeholder="Street Address" 
                  value={bookingData.address.line1} 
                  onChange={e => setBookingData(prev => ({...prev, address: {...prev.address, line1: e.target.value}}))} 
                  className="w-full px-4 py-3 border rounded-xl focus:ring-2 focus:ring-teal-500 outline-none" 
                />
                <div className="grid grid-cols-3 gap-4">
                  <input 
                    type="text" 
                    placeholder="City" 
                    value={bookingData.address.city} 
                    onChange={e => setBookingData(prev => ({...prev, address: {...prev.address, city: e.target.value}}))} 
                    className="px-4 py-3 border rounded-xl focus:ring-2 focus:ring-teal-500 outline-none" 
                  />
                  <input 
                    type="text" 
                    placeholder="State" 
                    value={bookingData.address.state} 
                    onChange={e => setBookingData(prev => ({...prev, address: {...prev.address, state: e.target.value}}))} 
                    className="px-4 py-3 border rounded-xl focus:ring-2 focus:ring-teal-500 outline-none" 
                  />
                  <input 
                    type="text" 
                    placeholder="ZIP" 
                    value={bookingData.address.zip} 
                    onChange={e => setBookingData(prev => ({...prev, address: {...prev.address, zip: e.target.value}}))} 
                    className="px-4 py-3 border rounded-xl focus:ring-2 focus:ring-teal-500 outline-none" 
                  />
                </div>
              </div>
              <textarea 
                placeholder="Special instructions (optional)..." 
                value={bookingData.notes} 
                onChange={e => setBookingData(prev => ({...prev, notes: e.target.value}))} 
                className="w-full p-4 border rounded-xl focus:ring-2 focus:ring-teal-500 outline-none resize-none" 
                rows={3} 
              />
            </div>
            <div>
              <div className="bg-slate-50 rounded-2xl p-6 sticky top-6">
                <h3 className="text-lg font-semibold text-slate-800 mb-4">Booking Summary</h3>
                <div className="flex items-center gap-4 mb-6 pb-6 border-b">
                  <img src={p.avatarUrl || `https://ui-avatars.com/api/?name=${encodeURIComponent(p.name || 'Provider')}&background=0d9488&color=fff`} alt={p.name} className="w-16 h-16 rounded-xl object-cover" />
                  <div>
                    <p className="font-semibold text-slate-800">{p.name || `${p.firstName} ${p.lastName}`}</p>
                    <p className="text-slate-500 text-sm">{p.providerType?.name || p.providerType}</p>
                  </div>
                </div>
                <div className="space-y-3 mb-6">
                  <div className="flex items-center gap-3 text-slate-600">
                    <Calendar className="w-5 h-5 text-teal-600" />
                    {bookingData.date ? formatDate(bookingData.date) : 'Select a date'}
                  </div>
                  <div className="flex items-center gap-3 text-slate-600">
                    <Clock className="w-5 h-5 text-teal-600" />
                    {bookingData.time || 'Select a time'} • {bookingData.duration} hr(s)
                  </div>
                  <div className="flex items-center gap-3 text-slate-600">
                    <MapPin className="w-5 h-5 text-teal-600" />
                    In-Home Visit
                  </div>
                </div>
                <div className="border-t pt-4 space-y-2">
                  <div className="flex justify-between text-slate-600">
                    <span>Subtotal</span>
                    <span>${((p.hourlyRate || 0) * bookingData.duration).toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between text-slate-600">
                    <span>Service Fee (5%)</span>
                    <span>${((p.hourlyRate || 0) * bookingData.duration * 0.05).toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between text-lg font-bold text-slate-800 pt-2 border-t">
                    <span>Total</span>
                    <span>${calculateTotal()}</span>
                  </div>
                </div>
                <button 
                  onClick={() => setView('payment')} 
                  disabled={!bookingData.date || !bookingData.time || !bookingData.address.line1} 
                  className={`w-full py-4 mt-6 rounded-xl font-semibold ${bookingData.date && bookingData.time && bookingData.address.line1 ? 'bg-gradient-to-r from-teal-600 to-emerald-500 text-white' : 'bg-slate-200 text-slate-400 cursor-not-allowed'}`}
                >
                  Continue to Payment
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BookingView;
